import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, NavigationRightIcon, Text, useResponsive } from '@common-fe/common-fe';
import _ from 'lodash';

import { REGEXPS } from '@/common';
import { CURRENCY_SIGN } from '@/common/constants';
import { useMyEnrollments } from '@/modules/transaction';

import AutoInvestmentReminder, {
  useAutoInvestmentReminder,
} from '../../AutoInvestment/AutoInvestmentReminder';
import { InvestmentCardBlock } from '../../InvestmentCardBlock';
import { useDefaultAllocationAmount } from '../../NewInvestments/hook';
import { useSelfDirectedAllocationValidation } from '../SelfDirectedAllocation/hooks';
import SelfDirectedAllocationFunds from '../SelfDirectedAllocation/SelfDirectedAllocationFunds';
import SelfDirectedInvestAmount from '../SelfDirectedAllocation/SelfDirectedInvestAmount';
import { useSelectedFundsStore } from '../stores';

import SelfDIrectedInvestMoreWrapper from './SelfDirectedInvestMoreWrapper';
interface Props {
  accountId: string;
  investorId?: string;
}
export const SelfDirectedInvestMore: React.FC<Props> = ({ accountId, investorId }) => {
  const { isMobile } = useResponsive();
  const defaultInitialAmount = useDefaultAllocationAmount();
  const [value, setValue] = useState(defaultInitialAmount || '');
  const [isSubmitted, setSubmitted] = useState(false);
  const valueToNumber = useMemo(() => {
    const clearedValue = value.replace(REGEXPS.ALL_COMMAS_REGEXP, '').replace(CURRENCY_SIGN, '');
    return _.toNumber(clearedValue);
  }, [value]);
  const { maxAmount, errorText } = useSelfDirectedAllocationValidation({
    accountId,
    investorId,
    value: valueToNumber,
  });
  const setSelectedFunds = useSelectedFundsStore((state) => state.setSelectedFunds);
  const { isDisplayedReminder } = useAutoInvestmentReminder({ accountId });
  const { t } = useTranslation();
  const { currentMyAccount } = useMyEnrollments(accountId);
  useEffect(() => {
    return () => {
      setSelectedFunds([]);
    };
  }, [setSelectedFunds]);
  return (
    <SelfDIrectedInvestMoreWrapper currentStep={1}>
      <Box pad={isMobile ? { horizontal: 'spacing16' } : {}}>
        <Heading level={1} size={isMobile ? 'mobile' : 'regular'} data-testid="invest-more_title">
          {t('Invest More')}
        </Heading>

        <Text size="large">
          {t('Set your investment amount and distribute it across your selected investments.')}
        </Text>
        <Text size="large">
          {t(
            'The selections you make here will apply only to this purchase and will not rebalance your current portfolio.'
          )}
        </Text>
      </Box>
      <Box
        direction={isMobile ? 'column' : 'row'}
        margin={isMobile ? { top: 'spacing8' } : {}}
        background="module"
        round="moduleRound"
        pad={isMobile ? 'spacing16' : 'spacing24'}
      >
        <InvestmentCardBlock
          enrollment={currentMyAccount}
          {...(isMobile
            ? {
                width: {
                  min: 'auto',
                },
              }
            : {})}
        />
        <Box
          pad={isMobile ? { vertical: 'spacing8' } : { horizontal: 'spacing12' }}
          align="center"
          justify="center"
        >
          <NavigationRightIcon style={isMobile ? { transform: 'rotate(90deg)' } : undefined} />
        </Box>
        <Box direction="column" justify="center">
          <SelfDirectedInvestAmount
            value={value}
            errorText={errorText}
            maxValue={maxAmount}
            isSubmitted={isSubmitted}
            onSetValue={setValue}
          />

          {isDisplayedReminder && <AutoInvestmentReminder accountId={accountId} />}
        </Box>
      </Box>
      <SelfDirectedAllocationFunds
        isErrors={!!errorText}
        isInvestMoreMode
        investorId={investorId}
        accountId={accountId}
        investAmount={valueToNumber}
        isSubmitted={isSubmitted}
        onSetSubmitted={setSubmitted}
      />
    </SelfDIrectedInvestMoreWrapper>
  );
};

export default SelfDirectedInvestMore;
